import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SecondApi } from "../api/secondApi";
import { getSecondApiLocale } from "../helper/apiLocale";
import useAuth from "../helper/useAuth";

function formatAmount(value, lang) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  try {
    return new Intl.NumberFormat(lang === "hy" ? "hy-AM" : "en-US").format(
      Number(value),
    );
  } catch (e) {
    return String(value);
  }
}

function Packages({ openModal } = {}) {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customSelectedIds, setCustomSelectedIds] = useState(() => new Set());

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    setError(null);
    const apiLocale = getSecondApiLocale(i18n.language);
    try {
      const { data } = await SecondApi.get(`/api/${apiLocale}/get_packages`);
      const list = Array.isArray(data?.data) ? data.data : [];
      setPackages(list);
    } catch (e) {
      if (e.code === "ERR_NETWORK" || !e.response) {
        setError(t("packages.errors.network"));
      } else {
        const msg = e.response?.data?.message;
        setError(
          typeof msg === "string" && msg.trim()
            ? msg
            : t("packages.errors.generic"),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [i18n.language, t]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const handleSelectPackage = () => {
    if (typeof openModal !== "function") return;
    openModal(isAuthenticated ? "getDemo" : "register");
  };

  const handleToggleCustomModule = (moduleId) => {
    if (moduleId == null) return;
    setCustomSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) next.delete(moduleId);
      else next.add(moduleId);
      return next;
    });
  };

  const handleSelectCustom = () => {
    if (typeof openModal !== "function") return;
    // When the backend exposes a build-package endpoint, swap this CTA to
    // submit `Array.from(customSelectedIds)`. For now we surface the same
    // demo/register flow used by predefined packages.
    openModal(isAuthenticated ? "getDemo" : "register");
  };

  // The "popular" item — middle one if there are 3+, otherwise none.
  const popularIndex = packages.length >= 3 ? 1 : -1;

  // Build a deduplicated list of all modules across packages.
  // Source of row labels for the comparison table.
  const allModules = useMemo(() => {
    const seen = new Map();
    for (const pkg of packages) {
      const mods = Array.isArray(pkg?.modules) ? pkg.modules : [];
      for (const m of mods) {
        const key =
          m?.module_id != null ? `id:${m.module_id}` : `name:${m?.module_name}`;
        if (!seen.has(key)) {
          seen.set(key, {
            id: m?.module_id ?? null,
            name: m?.module_name || "",
          });
        }
      }
    }
    return Array.from(seen.values()).filter((m) => Boolean(m.name));
  }, [packages]);

  return (
    <section
      id="packages"
      className="packages padding-y-120 position-relative z-index-1"
    >
      <style>{`
        .packages-table-wrap {
          overflow-x: auto;
          overflow-y: visible;
          padding: 8px 4px 6px;
          margin: 0 -4px;
          -webkit-overflow-scrolling: touch;
        }
        .packages-table-wrap::-webkit-scrollbar {
          height: 8px;
        }
        .packages-table-wrap::-webkit-scrollbar-thumb {
          background: hsl(var(--border-color));
          border-radius: 4px;
        }

        .packages-table {
          width: 100%;
          min-width: 880px;
          border-collapse: separate;
          border-spacing: 0;
          background: hsl(var(--white));
          border-radius: 18px;
          overflow: hidden;
          box-shadow: var(--box-shadow);
        }

        /* ---------- Feature column (left, sticky) ---------- */
        .packages-table .feature-col {
          position: sticky;
          left: 0;
          z-index: 2;
          min-width: 240px;
          background: hsl(var(--white));
          text-align: left;
          padding: 18px 22px;
          font-size: 14px;
          color: hsl(var(--heading-color));
          border-right: 1px solid hsl(var(--border-color));
        }
        .packages-table thead .feature-col {
          background: hsl(var(--main));
          color: #ffffff;
          z-index: 3;
        }
        .packages-table thead .feature-col .title {
          font-size: 18px;
          font-weight: 700;
          letter-spacing: 0.02em;
        }
        .packages-table thead .feature-col .hint {
          font-size: 12px;
          opacity: 0.85;
          margin-top: 4px;
          display: block;
        }
        .packages-table tfoot .feature-col {
          background: hsl(var(--white));
          border-top: 1px solid hsl(var(--border-color));
        }

        /* ---------- Package columns ---------- */
        .packages-table .pkg-col {
          padding: 22px 18px;
          text-align: center;
          min-width: 170px;
          vertical-align: middle;
          background: hsl(var(--white));
          color: hsl(var(--heading-color));
          font-size: 14px;
          border-bottom: 1px solid hsl(var(--border-color));
        }
        .packages-table tbody tr:last-child .pkg-col,
        .packages-table tbody tr:last-child .feature-col {
          border-bottom: 1px solid hsl(var(--border-color));
        }
        .packages-table .pkg-col + .pkg-col,
        .packages-table .feature-col + .pkg-col {
          border-left: 1px solid hsl(var(--border-color));
        }

        /* ---------- Header (package name + price + tariffs) ---------- */
        .packages-table thead .pkg-col {
          background: hsl(var(--main) / 0.92);
          color: #ffffff;
          border-bottom: none;
          padding: 24px 18px;
        }
        .packages-table thead .pkg-col.is-popular {
          background: var(--main-gradient);
        }
        .packages-table thead .pkg-col + .pkg-col {
          border-left: 1px solid hsl(0 0% 100% / 0.18);
        }
        .packages-table .pkg-name {
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          opacity: 0.92;
          margin-bottom: 6px;
        }
        .packages-table .pkg-price {
          font-size: 32px;
          font-weight: 700;
          line-height: 1.1;
          margin-bottom: 4px;
        }
        .packages-table .pkg-price .currency {
          font-size: 14px;
          font-weight: 500;
          margin-left: 3px;
          opacity: 0.9;
        }
        .packages-table .pkg-tariffs {
          margin-top: 10px;
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 4px;
        }
        .packages-table .pkg-tariff-pill {
          font-size: 11px;
          font-weight: 600;
          padding: 3px 8px;
          border-radius: 999px;
          background: hsl(0 0% 100% / 0.18);
          color: #ffffff;
          white-space: nowrap;
        }
        .packages-table .popular-badge {
          display: inline-block;
          background: #ffffff;
          color: hsl(var(--main));
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          padding: 4px 10px;
          border-radius: 999px;
          margin-bottom: 10px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }
        .packages-table thead .pkg-col:not(.is-popular) .pkg-head-inner::before {
          content: "";
          display: block;
          height: 22px;
        }
        .packages-table .pkg-head-inner {
          position: relative;
        }

        /* ---------- Cell yes/no ---------- */
        .packages-table .cell-check,
        .packages-table .cell-cross {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          font-size: 13px;
          line-height: 1;
        }
        .packages-table .cell-check {
          background: hsl(var(--main) / 0.12);
          color: hsl(var(--main));
        }
        .packages-table .cell-cross {
          background: hsl(var(--body-color) / 0.08);
          color: hsl(var(--body-color));
          opacity: 0.55;
        }

        /* ---------- Footer (Buy Now buttons) ---------- */
        .packages-table tfoot .pkg-col {
          background: hsl(var(--white));
          padding: 22px 14px;
          border-top: 1px solid hsl(var(--border-color));
          border-bottom: none;
        }
        .packages-table .pkg-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          background: hsl(var(--main));
          color: #ffffff;
          border: none;
          font-size: 13px;
          font-weight: 600;
          padding: 11px 18px;
          border-radius: 999px;
          cursor: pointer;
          transition: all 180ms ease;
          width: 100%;
          max-width: 180px;
        }
        .packages-table .pkg-cta:hover {
          background: hsl(var(--main-d-100));
          transform: translateY(-1px);
          box-shadow: 0 6px 14px hsl(var(--main) / 0.35);
        }
        .packages-table .pkg-cta.is-popular-cta {
          background: var(--main-gradient);
        }
        .packages-table .pkg-cta.is-popular-cta:hover {
          background: var(--main-gradient-rev);
        }

        /* ---------- Zebra rows (subtle) ---------- */
        .packages-table tbody tr:nth-child(even) .pkg-col,
        .packages-table tbody tr:nth-child(even) .feature-col {
          background: hsl(var(--section-bg));
        }

        /* ---------- Custom column (visually identical to others) ---------- */
        .packages-table .pkg-price--custom {
          font-size: 22px;
          font-weight: 700;
          letter-spacing: 0.02em;
        }
        .packages-table .pkg-tariff-pill--custom {
          background: hsl(0 0% 100% / 0.22);
          font-size: 11px;
          padding: 4px 10px;
          white-space: nowrap;
        }
        .packages-table .pkg-tariff-pill--custom .las {
          font-size: 12px;
          vertical-align: -1px;
        }

        /* ---------- Custom checkbox ---------- */
        .custom-checkbox {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 26px;
          height: 26px;
          min-width: 26px;
          min-height: 26px;
          max-width: 26px;
          max-height: 26px;
          flex-shrink: 0;
          border-radius: 7px;
          border: 2px solid hsl(var(--border-color));
          background: hsl(var(--white));
          cursor: pointer;
          position: relative;
          user-select: none;
          box-sizing: border-box;
          vertical-align: middle;
          transition:
            background-color 180ms ease,
            border-color 180ms ease,
            box-shadow 180ms ease;
        }
        .custom-checkbox:hover:not(.is-disabled) {
          border-color: hsl(var(--main));
        }
        .custom-checkbox.is-checked {
          background: hsl(var(--main));
          border-color: hsl(var(--main));
        }
        .custom-checkbox.is-disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .custom-checkbox__input {
          position: absolute;
          inset: 0;
          opacity: 0;
          margin: 0;
          padding: 0;
          cursor: inherit;
        }
        .custom-checkbox__visual {
          width: 100%;
          height: 100%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          font-size: 12px;
          line-height: 1;
          pointer-events: none;
        }
        /* Focus ring via box-shadow so it does not affect layout */
        .custom-checkbox:focus-within {
          box-shadow: 0 0 0 3px hsl(var(--main) / 0.22);
        }

        /* ---------- Custom footer (counter + CTA) ---------- */
        .custom-footer {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }
        .custom-counter {
          font-size: 12px;
          font-weight: 600;
          color: hsl(var(--body-color));
          transition: color 200ms ease;
        }
        .custom-counter.is-active {
          color: hsl(var(--main));
        }

        /* ---------- Mobile hint ---------- */
        .packages-table-hint {
          display: none;
          text-align: center;
          font-size: 12px;
          color: hsl(var(--body-color));
          margin-top: 12px;
        }
        @media (max-width: 767.98px) {
          .packages-table-hint {
            display: block;
          }
          .packages-table .feature-col {
            min-width: 180px;
            padding: 14px 16px;
          }
          .packages-table .pkg-col {
            min-width: 140px;
            padding: 16px 12px;
          }
          .packages-table thead .pkg-col {
            padding: 18px 12px;
          }
          .packages-table .pkg-price {
            font-size: 24px;
          }
          .packages-table .pkg-price--custom {
            font-size: 18px;
          }
        }
      `}</style>

      <div className="container container-two">
        <div className="section-heading style-center mb-64">
          <h3 className="section-heading__title">{t("packages.title")}</h3>
          <p className="section-heading__desc">{t("packages.description")}</p>
        </div>

        {error && !loading && (
          <div className="text-center mb-40">
            <div className="alert alert-danger d-inline-block font-14">
              {error}{" "}
              <button
                type="button"
                onClick={fetchPackages}
                className="link text-decoration-underline fw-500 bg-transparent border-0 p-0 ms-1"
              >
                {t("packages.retry")}
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <PackagesTableSkeleton />
        ) : packages.length === 0 && !error ? (
          <div className="text-center text-body py-5">{t("packages.empty")}</div>
        ) : (
          <>
            <div className="packages-table-wrap">
              <PackagesComparisonTable
                packages={packages}
                modules={allModules}
                popularIndex={popularIndex}
                onSelectPackage={handleSelectPackage}
                customSelectedIds={customSelectedIds}
                onToggleCustomModule={handleToggleCustomModule}
                onSelectCustom={handleSelectCustom}
                isAuthenticated={isAuthenticated}
              />
            </div>
            <div className="packages-table-hint">
              <i className="las la-arrows-alt-h" />{" "}
              {i18n.language === "hy"
                ? "Աղյուսակը հնարավոր է թերթել կողքից"
                : "Scroll the table horizontally"}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function PackagesComparisonTable({
  packages,
  modules,
  popularIndex,
  onSelectPackage,
  customSelectedIds,
  onToggleCustomModule,
  onSelectCustom,
  isAuthenticated,
}) {
  const { t, i18n } = useTranslation();

  const hasModule = (pkg, moduleId, moduleName) => {
    const list = Array.isArray(pkg?.modules) ? pkg.modules : [];
    return list.some((m) => {
      if (moduleId != null && m.module_id === moduleId) return true;
      if (!moduleId && m.module_name && m.module_name === moduleName) {
        return true;
      }
      return false;
    });
  };

  const customCount = customSelectedIds?.size ?? 0;

  return (
    <table className="packages-table" role="table">
      <thead>
        <tr>
          <th className="feature-col" scope="col">
            <span className="title">{t("packages.featuresColLabel")}</span>
            <span className="hint">{t("packages.featuresColHint")}</span>
          </th>
          {packages.map((pkg, idx) => {
            const popular = idx === popularIndex;
            const tariffs = Array.isArray(pkg?.tariffs) ? pkg.tariffs : [];
            return (
              <th
                key={pkg.id ?? idx}
                scope="col"
                className={`pkg-col${popular ? " is-popular" : ""}`}
              >
                <div className="pkg-head-inner">
                  {popular && (
                    <span className="popular-badge">
                      {t("packages.popular")}
                    </span>
                  )}
                  <div className="pkg-name">
                    {pkg?.name || t("packages.fallbackName")}
                  </div>
                  <div className="pkg-price">
                    {formatAmount(pkg?.price, i18n.language)}
                    <span className="currency">{t("packages.currency")}</span>
                  </div>
                  {tariffs.length > 0 && (
                    <div className="pkg-tariffs">
                      {tariffs.map((tf) => (
                        <span
                          key={tf.id ?? `${tf.days}-${tf.price_per_day}`}
                          className="pkg-tariff-pill"
                        >
                          {t("packages.tariffShort", { days: tf.days })}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </th>
            );
          })}
          {/* Custom column header */}
          <th scope="col" className="pkg-col is-custom">
            <div className="pkg-head-inner">
              <div className="pkg-name">{t("packages.customColTitle")}</div>
              <div className="pkg-price pkg-price--custom">
                {t("packages.customColPrice")}
              </div>
              <div className="pkg-tariffs">
                <span className="pkg-tariff-pill pkg-tariff-pill--custom">
                  <i className="las la-sliders-h me-1" />
                  {t("packages.customColTariff")}
                </span>
              </div>
            </div>
          </th>
        </tr>
      </thead>

      <tbody>
        {modules.map((mod) => (
          <tr key={mod.id ?? mod.name}>
            <th scope="row" className="feature-col">
              {mod.name}
            </th>
            {packages.map((pkg, idx) => {
              const included = hasModule(pkg, mod.id, mod.name);
              return (
                <td key={pkg.id ?? idx} className="pkg-col">
                  {included ? (
                    <span
                      className="cell-check"
                      title={t("packages.includedYes")}
                      aria-label={t("packages.includedYes")}
                    >
                      <i className="fas fa-check" />
                    </span>
                  ) : (
                    <span
                      className="cell-cross"
                      title={t("packages.includedNo")}
                      aria-label={t("packages.includedNo")}
                    >
                      <i className="fas fa-minus" />
                    </span>
                  )}
                </td>
              );
            })}
            {/* Custom column cell — checkbox */}
            <td className="pkg-col is-custom-cell">
              <CustomCheckbox
                checked={
                  mod.id != null ? Boolean(customSelectedIds?.has(mod.id)) : false
                }
                onChange={() => onToggleCustomModule(mod.id)}
                ariaLabel={mod.name}
                disabled={mod.id == null}
              />
            </td>
          </tr>
        ))}
      </tbody>

      <tfoot>
        <tr>
          <td className="feature-col" />
          {packages.map((pkg, idx) => {
            const popular = idx === popularIndex;
            return (
              <td key={pkg.id ?? idx} className="pkg-col">
                <button
                  type="button"
                  onClick={() => onSelectPackage(pkg)}
                  className={`pkg-cta${popular ? " is-popular-cta" : ""}`}
                >
                  {t("packages.selectPackage")}
                  <i className="las la-arrow-right" />
                </button>
              </td>
            );
          })}
          {/* Custom column footer */}
          <td className="pkg-col is-custom-cell">
            <div className="custom-footer">
              <span
                className={`custom-counter${
                  customCount > 0 ? " is-active" : ""
                }`}
              >
                {t("packages.customSelectedCount", { count: customCount })}
              </span>
              <button
                type="button"
                onClick={onSelectCustom}
                className="pkg-cta"
                disabled={customCount === 0}
              >
                {isAuthenticated
                  ? t("packages.customCta")
                  : t("packages.customCtaUnauthenticated")}
                <i className="las la-arrow-right" />
              </button>
            </div>
          </td>
        </tr>
      </tfoot>
    </table>
  );
}

function CustomCheckbox({ checked, onChange, ariaLabel, disabled }) {
  return (
    <label
      className={`custom-checkbox${checked ? " is-checked" : ""}${
        disabled ? " is-disabled" : ""
      }`}
    >
      <input
        type="checkbox"
        className="custom-checkbox__input"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        aria-label={ariaLabel}
      />
      <span className="custom-checkbox__visual" aria-hidden="true">
        {checked && <i className="fas fa-check" />}
      </span>
    </label>
  );
}

function PackagesTableSkeleton() {
  return (
    <div
      className="packages-table-wrap"
      style={{ filter: "blur(0.5px)", opacity: 0.7 }}
    >
      <table className="packages-table">
        <thead>
          <tr>
            <th className="feature-col">
              <span
                className="d-inline-block bg-light rounded"
                style={{ width: 120, height: 20 }}
              />
            </th>
            {[0, 1, 2, 3].map((i) => (
              <th key={i} className="pkg-col">
                <span
                  className="d-inline-block bg-light rounded mb-2"
                  style={{ width: 80, height: 14, opacity: 0.5 }}
                />
                <br />
                <span
                  className="d-inline-block bg-light rounded"
                  style={{ width: 70, height: 28, opacity: 0.5 }}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <tr key={i}>
              <td className="feature-col">
                <span
                  className="d-inline-block bg-light rounded"
                  style={{ width: "70%", height: 14 }}
                />
              </td>
              {[0, 1, 2, 3].map((j) => (
                <td key={j} className="pkg-col">
                  <span
                    className="d-inline-block bg-light rounded-circle"
                    style={{ width: 28, height: 28 }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default Packages;
